import { defineStore } from 'pinia';
import { ref } from 'vue';
import { changeProfileApi } from '@/shared/api/changeProfileApi';

/** CSP 프로필에서는 막고 원래 회사에서만 쓰는 프로필 메뉴(C69) — Account·Billing·Authorization Settings, Users, Inbox */
const OWNER_ONLY_HREFS = new Set([
  '/pages/account-settings',
  '/settings/billing',
  '/pages/authorization-settings',
  '/users',
  '/pages/inbox',
]);

/**
 * Change Profile 메뉴 표시 여부 + 팝업 열림 상태.
 * 우측 ProfileDD 와 좌측 SidebarProfile 이 같은 틱에 마운트되므로 진행 중 Promise 를 공유해 /status 를 한 번만 부름.
 */
export const useChangeProfileStore = defineStore('changeProfile', () => {
  const enabled = ref(false);
  // CSP 프로필로 전환한 상태 — 원래 회사 전용 메뉴(설정·사용자·Inbox)를 막는 데 씀(C69)
  const inProfile = ref(false);
  // 원래 회사 이름 — 원래 회사 전용 메뉴 안내 문구에 표시
  const ownerName = ref('');
  const loaded = ref(false);
  const isOpen = ref(false);

  let statusPromise: Promise<void> | null = null;
  // 진행 중 요청 — 같은 틱에 마운트되는 ProfileDD(헤더)·SidebarProfile 이 refresh 를 같이 불러도 요청 1번
  let inFlight = false;

  /**
   * refresh: 프로필 메뉴가 마운트될 때 true — 로그아웃은 새로고침이 없어 다음 로그인 때 이전 회사 결과가 남으므로
   * 메뉴가 새로 뜰 때(= 로그인 직후)마다 다시 물음. Entity Live 판정(useCspClient)은 refresh 없이 마지막 결과 공유
   */
  function loadStatus(opts?: { refresh?: boolean }): Promise<void> {
    if (!statusPromise || (opts?.refresh && !inFlight)) {
      inFlight = true;
      statusPromise = changeProfileApi.status()
        .then((v) => {
          enabled.value = v.enabled;
          inProfile.value = v.inProfile;
          ownerName.value = v.ownerName;
          loaded.value = true;
        })
        .finally(() => { inFlight = false; });
    }
    return statusPromise;
  }

  /** CSP 프로필로 전환한 상태에서 원래 회사 전용 메뉴인지 */
  function isOwnerOnlyBlocked(href?: string): boolean {
    return inProfile.value && !!href && OWNER_ONLY_HREFS.has(href);
  }

  function open() {
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
  }

  return { enabled, inProfile, ownerName, loaded, isOpen, loadStatus, isOwnerOnlyBlocked, open, close };
});
